import i18n from 'simple-react-i18n'
import moment from 'moment'
import { orderBy, partition, take } from 'lodash'
import Line from '../../../../components/echart/series/Line'
import MultiBand from '../../../../components/echart/series/MultiBand'
import PiezometerStationAction from '../../../../station/actions/PiezometerStationAction'
import { MAP_SITUATION_CLASS } from '../../../../station/constants/piezo/PiezometerStationConstants'
import { MEASURE_COTE } from '../../../constants/PiezometryConstants'
import { round } from '../../../../utils/NumberUtil'
import { getFrequencyStats } from './SuiviStatsUtils'

const seriesProps = () => [
    { code: 'ENVELOPPE_AVERAGE', name: i18n.enveloppeAvg, color: 'green', nb: 0.5 },
    { code: 'ENVELOPPE_MEDIAN', name: i18n.enveloppeMedian, color: 'orange' },
    { code: 'ENVELOPPE_MIN', name: i18n.enveloppeMin, color: 'grey', nb: 0 },
    { code: 'ENVELOPPE_MAX', name: i18n.enveloppeMax, color: 'grey', nb: 1 },
]

const RETURN_PERIOD_STATS = ['PERIOD_5_H', 'PERIOD_3_H', 'ENVELOPPE_AVERAGE', 'PERIOD_3_S', 'PERIOD_5_S']

const buildPiezoStatsSeries = ({
    id,
    typeId,
    statSeries = [],
    displayCote,
    isPiezo = true,
    landmarkValue,
    histoYears,
    validOnly,
    startDate,
    endDate,
    linearMode = false,
    minY,
    axisIndexes = {},
}) => {
    const toEchartMeasure = (m) => ({
        value: [
            moment(m[0]).hour(12).valueOf(),
            isPiezo ? (displayCote === MEASURE_COTE.NGF ? m[1] : round(landmarkValue - m[1], 3)) : m[1],
            { NGF: m[1], depth: round(landmarkValue - m[1]) },
        ], isPiezo,
    })

    const baseInput = {
        stationId: id,
        displayCote: isPiezo ? MEASURE_COTE.NGF : undefined,
        dataType: typeId,
        validOnly,
        calculationStartDate: startDate,
        calculationEndDate: endDate,
        chartMode: true,
    }
    const endEnveloppe = moment().add(1, 'year').endOf('year').valueOf()
    const allInputs = statSeries.reduce((acc, stat) => {
        if (stat === 'ENVELOPPE') {
            return [...acc, { ...baseInput, groupFunc: 'ENVELOPPE_MIN', endEnveloppe }, { ...baseInput, groupFunc: 'ENVELOPPE_MAX', endEnveloppe }]
        }
        if (stat.includes('PERIOD')) {
            return [...acc, { ...baseInput, groupFunc: stat, endEnveloppe }]
        }
        if (stat.startsWith('ENVELOPPE_AVERAGE')) {
            return [...acc, { ...baseInput, groupFunc: 'ENVELOPPE_AVERAGE', endEnveloppe }]
        }
        if (stat.startsWith('ENVELOPPE_MEDIAN')) {
            return [...acc, { ...baseInput, groupFunc: 'ENVELOPPE_MEDIAN', endEnveloppe }]
        }
        return [...acc, { ...baseInput, groupFunc: stat }]
    }, [])

    const promises = allInputs.map(p => PiezometerStationAction.promisePiezoChartMeasures(p))
    return Promise.all(promises).then(jsonTab => {
        const allSeries = allInputs.map((s, idx) => ({ stat: s.groupFunc, json: jsonTab[idx] }))
        const [bandSeries, others] = partition(allSeries, s => {
            if (linearMode) {
                return false
            }
            return !s.stat.includes('AVERAGE') && !s.stat.includes('MEDIAN') && (s.stat.includes('ENVELOPPE') || s.stat.includes('PERIOD'))
        })
        const periods = allSeries.filter(s => s.stat.includes('PERIOD')).map(s => s.stat.split('_')[1])
        const key = displayCote === MEASURE_COTE.NGF ? 'NGF' : 'depth'

        const bandsWithProps = bandSeries.map(b => ({ ...b, ...[...getFrequencyStats(histoYears, periods), ...seriesProps()].find(p => b.stat.replace('PERIOD', 'fre') === p.code) }))

        const hasPeriods = bandsWithProps.some(s => s.stat.includes('PERIOD'))

        const bands = (() => {
            if (!bandsWithProps.length) {
                return []
            } else if (!hasPeriods) {
                return bandsWithProps.map((b, idx) => ({
                    color: 'grey',
                    isPiezo,
                    showSymbol: false,
                    ...b,
                    data: b.json.map(m => toEchartMeasure(m)),
                    idx,
                    lineStyle: {
                        normal: {
                            width: 1,
                        },
                    },
                }))
            }
            const statsColors = orderBy(take(MAP_SITUATION_CLASS, bandsWithProps.length + 1), 'order', 'asc')
            const ordered = orderBy(bandsWithProps, 'nb', 'asc')
            const lowerBand = { ...ordered[0], json: ordered[0].json.map(d => [d[0], minY, d[2], d[3]]), noYScale: true, otherProps: { NGF: { noLegend: true, noTooltip: true }, depth: {} } }
            const upperBand = { ...ordered[ordered.length - 1], json: ordered[0].json.map(d => [d[0], 600, d[2], d[3]]), noYScale: true, otherProps: { NGF: {}, depth: { noLegend: true, noTooltip: true } } }
            const baseTab = ([lowerBand, ...ordered, upperBand])
            const returned = baseTab.map((b, idx) => ({
                isPiezo,
                showSymbol: false,
                ...b,
                initialName: b.name,
                NGFName: idx === baseTab.length - 1 ? `> ${b.name}` : `< ${b.name}`,
                depthName: idx === baseTab.length - 1 ? `> ${baseTab[idx + 1]?.name}` : `< ${baseTab[idx + 1]?.name}`,
                depthColor: statsColors[idx]?.chartColor || '#00008b66',
                NGFColor: statsColors[idx - 1]?.chartColor || '#ff000066',
                data: b.json.map(m => toEchartMeasure(m)),
                idx,
                ...((b.otherProps ?? {})[key] ?? {}),
                isPeriod: true,
            }))
            return returned.map(b => ({ ...b, name: displayCote === MEASURE_COTE.NGF ? b.NGFName : b.depthName, color: displayCote === MEASURE_COTE.NGF ? b.NGFColor : b.depthColor }))
        })()

        const sortedBands = orderBy(bands, 'idx', displayCote === MEASURE_COTE.NGF ? 'asc' : 'desc')

        if (displayCote === MEASURE_COTE.DEPTH && hasPeriods && isPiezo) {
            sortedBands[1].data = sortedBands[1].data.map(d => ({ ...d, noDepthHackValue: true }))
            sortedBands[1].name = sortedBands[1].name.replace('<', '>')
            sortedBands[sortedBands.length - 1].data = sortedBands[sortedBands.length - 1].data.map(d => ({ ...d, noDepthHackValue: true }))
            sortedBands[sortedBands.length - 1].name = sortedBands[sortedBands.length - 1].name.replace('<', '>')
        }

        const multiBands = MultiBand({ bands: sortedBands, noSort: true, noGap: !isPiezo, ...axisIndexes })

        const otherSeries = others.map(serie => {
            const { stat, json } = serie
            const serieParams = [...getFrequencyStats(histoYears, periods), ...seriesProps()].find(p => stat.replace('PERIOD', 'fre') === p.code)
            return Line({
                data: json.map(m => toEchartMeasure(m)),
                name: serieParams.name,
                isPiezo,
                connectNulls: false,
                showSymbol: false,
                color: serieParams.color,
                ...axisIndexes,
                lineStyle: {
                    normal: {
                        color: serieParams.color,
                        type: 'dashed',
                        width: 1,
                    },
                },
            })
        })

        return [multiBands, ...otherSeries]
    })
}

export { buildPiezoStatsSeries, seriesProps, RETURN_PERIOD_STATS }